import { useCallback, useMemo, type ReactNode } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { FiPlus } from 'react-icons/fi';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import type { ListColumn } from '@/shared/app-list-layout/types';
import type { DataTableView } from '@/shared/app-list-layout';
import { formatDate } from '@/shared/format';
import { agreementsPlatformApi } from '@/modules/agreements/services/platform-api';
import {
  AgreementsList,
  documentStatusLabel,
  documentStatusTone,
  requiredActionLabel,
} from '@/modules/agreements/shared';
import type { AgreementDocumentResponse } from '@/api/schemas/agreements-platform';

/**
 * The platform's agreement documents.
 *
 * PLATFORM authority: the caller needs `platform.agreement.document.read`. A caller
 * without it receives 403, rendered as a refusal rather than an empty list — an empty
 * list would be indistinguishable from a deployment with no documents.
 *
 * The page renders through the shared `AppListLayout` with the same surface the tenant
 * directory carries: saved-view tabs with counts, a search box, column-bound filters,
 * numeric pagination and the table/cards toggle. The read is a whole-collection read —
 * `listDocuments` takes no query parameters — so the page answers the engine's query
 * locally, and `onFetch` stays the single data path.
 */

/** The publication states a document can be filtered by. */
const STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft' },
  { value: 'published', label: 'Published' },
  { value: 'withdrawn', label: 'Withdrawn' },
];

/** Who a document is addressed to. */
const AUDIENCE_OPTIONS = [
  { value: 'tenant', label: 'Tenant' },
  { value: 'customer', label: 'Customer' },
];

/** The fields a search term is matched against — what identifies a document to a human. */
function searchFields(row: AgreementDocumentResponse): ReadonlyArray<string> {
  return [row.title, row.documentKey, documentStatusLabel(row.status)];
}

/** Saved views rendered as tabs above the document list. */
function buildDocumentViews(counts: Partial<Record<string, number>>): DataTableView[] {
  return [
    { id: 'all', label: 'All documents', count: counts.all },
    { id: 'draft', label: 'Draft', filters: { status: 'draft' }, count: counts.draft },
    {
      id: 'published',
      label: 'Published',
      filters: { status: 'published' },
      count: counts.published,
    },
    {
      id: 'withdrawn',
      label: 'Withdrawn',
      filters: { status: 'withdrawn' },
      count: counts.withdrawn,
    },
  ];
}

/** The page's columns. Declarative descriptors — the engine renders every cell. */
function documentColumns(
  onOpen: (documentId: string) => void,
): ReadonlyArray<ListColumn<AgreementDocumentResponse>> {
  return [
    {
      id: 'title',
      header: 'Title',
      // The title identifies the document — it is the heading a card leads with.
      primary: true,
      sortable: true,
      cell: (row) => (
        <button
          type="button"
          className="text-left font-medium text-accent hover:underline"
          onClick={() => {
            onOpen(row.documentId);
          }}
        >
          {row.title}
        </button>
      ),
    },
    {
      id: 'key',
      header: 'Key',
      cell: (row) => <span className="font-mono text-xs text-content-muted">{row.documentKey}</span>,
    },
    {
      id: 'audience',
      header: 'Audience',
      filter: { type: 'select', options: AUDIENCE_OPTIONS, placeholder: 'Any' },
      cell: (row) => <span className="capitalize">{row.audience}</span>,
    },
    {
      id: 'action',
      header: 'Action',
      cell: (row) => requiredActionLabel(row.requiredAction),
    },
    {
      id: 'status',
      header: 'Status',
      sortable: true,
      filter: { type: 'select', options: STATUS_OPTIONS, placeholder: 'Any' },
      cell: (row) => (
        <Badge tone={documentStatusTone(row.status)}>{documentStatusLabel(row.status)}</Badge>
      ),
    },
    {
      id: 'updatedAt',
      header: 'Updated',
      sortable: true,
      cell: (row) => <span className="text-content-muted">{formatDate(row.updatedAt)}</span>,
    },
  ];
}

export function DocumentsListPage(): ReactNode {
  const navigate = useNavigate();

  const read = useCallback(
    () => agreementsPlatformApi.listDocuments().then(({ documents }) => documents),
    [],
  );

  const onOpen = useCallback(
    (documentId: string): void => {
      void navigate({
        to: '/platform/agreements/documents/$documentId',
        params: { documentId },
      });
    },
    [navigate],
  );
  const columns = useMemo(() => documentColumns(onOpen), [onOpen]);

  const openComposer = useCallback((): void => {
    void navigate({ to: '/platform/agreements/documents/new' });
  }, [navigate]);

  return (
    <AgreementsList<AgreementDocumentResponse>
      title="Agreement documents"
      description="Author a document, publish a version, then configure the requirement that asks for it."
      read={read}
      searchFields={searchFields}
      countKeyOf={(row) => row.status}
      buildViews={buildDocumentViews}
      columns={columns}
      getRowId={(row) => row.documentId}
      // Six columns run off a phone screen, so the page asks for the responsive
      // presentation: cards below `sm`, the table above.
      responsive
      toolbarActions={
        <Button size="sm" onClick={openComposer}>
          <FiPlus size={14} aria-hidden="true" />
          New document
        </Button>
      }
      emptyTitle="No agreement documents"
      emptyDescription="Author the first document, then publish a version and configure a requirement."
      noMatchDescription="No document matches this tab, search or filter."
      emptyAction={<Button onClick={openComposer}>New document</Button>}
    />
  );
}
